/**
 * The Add panel's "Sections"/"Blocks" accordion choice is persisted to localStorage (see
 * `openAccordion` in AddPanel.tsx) so the panel reopens on whichever the person had open last.
 *
 * `requestSectionsAccordionOpen` lets a caller outside AddPanel (SectionDividerMenu's "+" button,
 * which always adds a section — ENG-1652) pre-seed that value before the panel mounts, so it opens
 * on Sections instead of whatever was last used.
 *
 * This writes directly to localStorage rather than through AddPanel's own `useLocalStorage` setter:
 * that hook's actual `localStorage.setItem` call happens in a passive effect, which can run after
 * AddPanel's mount-time read of the same key when both land in the same commit. Writing here,
 * synchronously, before the caller dispatches the sidebar-page change that mounts AddPanel, avoids
 * that race.
 */
export const ADD_PANEL_OPEN_ACCORDION_KEY = 'presentationEditor.addPanel.openAccordion'

export type AddPanelAccordion = 'sections' | 'blocks'

/**
 * Fired after the stored value is written, for an AddPanel that is already mounted — its
 * `useLocalStorage` state only reads the key on mount. The guided tours (`tours/presentationTourSteps.ts`)
 * open Blocks while the panel may already be open on Sections.
 */
export const ADD_PANEL_ACCORDION_REQUEST_EVENT = 'presentationEditor:addPanelAccordionRequest'

export function requestAddPanelAccordionOpen(accordion: AddPanelAccordion) {
  try {
    window.localStorage.setItem(ADD_PANEL_OPEN_ACCORDION_KEY, JSON.stringify(accordion))
  } catch (error) {
    // localStorage unavailable (private browsing, quota) — the panel still opens, it just won't
    // pre-expand the accordion on mount. The event below still reaches a mounted panel.
    console.warn(`requestAddPanelAccordionOpen: error setting key "${ADD_PANEL_OPEN_ACCORDION_KEY}"`, error)
  }

  window.dispatchEvent(new CustomEvent<AddPanelAccordion>(ADD_PANEL_ACCORDION_REQUEST_EVENT, { detail: accordion }))
}

export function requestSectionsAccordionOpen() {
  requestAddPanelAccordionOpen('sections')
}

/**
 * Fired after a section from the layout flyout lands on the canvas, by drop (`Presentation.tsx`'s
 * `handleDropSection`) or by click. The sidebar closes the flyout so the new section is not hidden
 * under it, and leaves the Sections list open for the next pick (#24863).
 */
export const SECTION_INSERTED_EVENT = 'presentationEditor:sectionInserted'

export function notifySectionInserted() {
  window.dispatchEvent(new CustomEvent(SECTION_INSERTED_EVENT))
}
